import { Injectable } from '@angular/core';
import { NgbModal }   from '@ng-bootstrap/ng-bootstrap';

import { ApiService }               from '../api/api.service';
import { CodeTypeModel }            from '../core/models/code-type';
import { CompanyIntegrationsModel } from '../companies/models/company.integrations';
import { CompanyService }           from '../companies/company.service';
import { InvoiceModel }             from '../invoices/models/invoice';
import { SessionModel }             from '../sessions/models/session';
import { SessionService }           from '../sessions/session.service';
import { StripeAccountLinkModel }   from './models/stripe.account-link';
import { StripeAccountModel }       from './models/stripe.account';
import { StripeCheckoutComponent }  from './stripe.checkout.component';
import { StripeSettingsModel }      from './models/stripe-settings';
import { XeroChartOfAccountsModel } from '../xero/models/xero.chart-of-accounts';
import { XeroService }              from '../xero/xero.service';

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  constructor (
    private apiService    : ApiService,
    private companyService: CompanyService,
    private modalService  : NgbModal,
    private sessionService: SessionService,
    private xeroService   : XeroService
  ) {}

  checkout ( invoice : InvoiceModel, token ?: string ) : Promise<stripe.paymentIntents.PaymentIntent> {
    return this.createPaymentIntent(invoice, token)
    .then(() => token
      ? this.apiService.get('/invoice/show_by_token/', [ token, false ])
        .then(( response : any ) => response.invoice)
      : this.apiService.get('/invoice', invoice.id)
    )
    .then(( invoice : InvoiceModel ) => {
      const modal = this.modalService.open(StripeCheckoutComponent);

      modal.componentInstance.invoice = invoice;

      return modal.result;
    });
  }

  connect () : Promise<StripeAccountLinkModel> {
    return Promise.resolve()
    .then(() => {
      return this.isLinked()
        ? Promise.resolve(true)
        : this.apiService.get('/stripe/create');
    })
    .then(() => this.apiService.get('/stripe/onboarding'));
  }

  createPaymentIntent ( invoice : InvoiceModel, token ?: string ) : Promise<stripe.paymentIntents.PaymentIntent> {
    return this.apiService.post(`/stripe/payment_intent/${ invoice.id }`, {
      id_token: token
    });
  }

  disable () : Promise<SessionModel> {
    return this.apiService.get('/company_integration/remove_stripe')
    /**
     * refresh the session because they may get redirected away
     * from the settings page after a disable
     * Reference: WOR-2560
     */
    .then(() => this.sessionService.refresh());
  }

  enable () : Promise<boolean> {
    return this.apiService.get('/company_integration/add_stripe');
  }

  get () : Promise<StripeAccountModel> {
    return this.apiService.get('/stripe/retrieve');
  }

  getAccountId () : string {
    return this.getSessionSettings().stripe_account_id;
  }

  getAccounts () : Promise<Array<CodeTypeModel>> {
    if (this.companyService.hasIntegration('xero')) {
      return this.xeroService.accounts()
      .then(( accounts : XeroChartOfAccountsModel ) => accounts.all_accounts);
    }
  }

  getPublicKey () : string {
    return this.getSessionSettings().stripe_public_key;
  }

  getSessionSettings () : StripeSettingsModel {
    return this.sessionService.company().stripe_settings;
  }

  getSurchargeLabel () : string {
    return this.getSessionSettings().stripe_surcharge_label;
  }

  hasAccounting () : boolean {
    return this.companyService.hasIntegration('xero');
  }

  hasSurcharges ( invoice : InvoiceModel ) : boolean {
    return !!invoice.stripe_fee_amount;
  }

  isActive ( account : StripeAccountModel ) : boolean {
    return this.isSubmitted(account)
      && account.charges_enabled;
  }

  isLinked () : boolean {
    return !!this.getSessionSettings().stripe_account_id;
  }

  isSubmitted ( account : StripeAccountModel ) : boolean {
    return this.isLinked()
      && account.details_submitted;
  }

  saveSettings ( settings : CompanyIntegrationsModel ) : Promise<CompanyIntegrationsModel> {
    return this.apiService.patch('/company_integrations', settings);
  }
}
